import { LitElement, html, nothing, svg } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../api';
import { ClipRecorder } from '../audio/recorder';
import { browserDetail, currentTarget, errorText, num, storedErrorText, t, tOr } from '../i18n';
import {
  blockOf, deleteDraft, dismissCompleted, dismissExpired, DraftSaveError, draftsView, makeClientId, needsHearing, MAX_TEXT_CHARS, outcomeOf,
  refreshDrafts, saveDraft, sendCapture, sendDrafts, subscribeDrafts,
  type Capture, type CompletedDraft, type Draft, type DraftBlock, type DraftKind, type DraftSaveOpts, type DraftsView, type SendResult,
} from '../drafts';
import { actionFor, completedState, expiredLine, fmtCreated, limitsHint, resetTime, savedLine, stateLine, stateOf, tryable, tzOf } from '../drafts-ui';
import { currentOwner, onSession, resolveSession, session, type Identity } from '../session';
import type { Allowance, ReviewGate } from '../types';
import { aiLine, aiOf, aiOut, aiReason, observeAllocationChange } from '../ai-allowance';
import { gateOf, gateRefusal, renderGate } from '../review-gate';
import { go } from '../router';
import { savedRecordingPath } from './capture-open';
import './wait-moment';
import type { WaitCapture } from './wait-moment';
import { skeletonLine } from './wait-bits';
import { oneButtonOn, takeQuillLine } from '../one-button';
import { personOf, questionOf, type NewcomerPerson, type NewcomerQuestion } from '../newcomers';
import { answerLabel, nameHtml } from './newcomer-name';
import { tPart } from '../bidi-html';
import {
  cancelCaptureAttempt, cancelWrittenAttempt, expectLessonDisplay, prepareRecordedAttempt, prepareWrittenAttempt,
  recordCaptureEvent, recordDisplayedBlocker, recordLocalDraftSaved, researchAttemptFor,
} from '../research-client';
import type { ResearchAttempt } from '../research/delivery';
import type { ResearchReason } from '../../../server/src/research/schema.js';
import { dropShared, measureClip, readShared } from '../share-inbox';
import { SHARED_MAX_BYTES, oggOpusSeconds, sharedRefusal, sniffAudioMime } from '../shared-clip.generated';
import { isIos } from '../reminder-state';

const MAX_SEC = 60;
/** Say it instead's microphone, drawn in the button's own colour. */
const MIC_ICON = html`<svg class="say-mic" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${svg`<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5.5 11a6.5 6.5 0 0 0 13 0"/><path d="M12 17.5V21"/><path d="M9 21h6"/>`}</svg>`;

/**
 * What this screen sends: a recording with its clip in memory, or a typed note with its text, so the wait always
 * has the learner's own moment to show (ticket 41). A retry that sends a recording without its clip must bring the
 * clip back (or give the wait another way to show it), not loosen this type.
 */
type Sent = Capture & ({ kind: 'audio'; blob: Blob } | { kind: 'text'; wanted: string });

/** Which way in the form opens on: recording what people said, or typing what the learner wanted to say. */
export type CaptureTab = 'record' | 'wanted';

/**
 * Record and Type: the capture form, its drafts, the allowance, the review gate and the wait (ticket 41). One
 * element, shown on the record screen (`/record`, record-view.ts) and in the panel that slides up over home's town
 * (capture-sheet.ts). A save goes to its landing (`/land/<id>`) from either.
 */
@customElement('capture-form')
export class CaptureForm extends LitElement {
  /** The tab to open on; unset, the URL's `#wanted` picks it (the record screen). */
  @property({ attribute: false }) opensOn?: CaptureTab | 'shared';
  /**
   * Answer them (v1 ticket 42): the newcomer moment whose question the typed note answers, from home's URL
   * (`/#type?answer=<id>`). The question shows above the note, and the note is sent with `answer`.
   */
  @property({ attribute: false }) answer = '';
  /** The question being answered, once read (null: the moment has none, or could not be read). */
  @state() private question?: NewcomerQuestion | null;
  /** Who asks (the revamp): the name (Finglish first, its script), the role; read with the question. */
  @state() private asker: NewcomerPerson | null = null;
  @state() private tab: CaptureTab = 'record';
  /**
   * Share to Eaves: a voice message stashed by the service worker, read when the form opens on `shared`. `ready`: the
   * clip waits in the review step; the others are the one line it says instead (nothing there, a type we cannot read,
   * too long, with the length when known).
   */
  @state() private shared: 'loading' | 'ready' | 'none' | 'unsupported' | 'too-long' = 'loading';
  @state() private sharedSec: number | null = null;
  @state() private phase: 'idle' | 'recording' | 'review' | 'uploading' = 'idle';
  @state() private level = 0;
  @state() private elapsed = 0;
  @state() private note = '';
  @state() private wanted = '';
  @state() private error = '';
  @state() private notice = '';
  @state() private paused = false;
  @state() private skipped = 0;
  @state() private skipSilence = true;
  /** The signed-in learner's drafts on this device (ticket 12). */
  @state() private drafts: DraftsView = draftsView();
  /** Draft id currently being tried ('__all__' while try-all runs). */
  @state() private busyId = '';
  /** Draft id whose delete is waiting for a confirm. */
  @state() private confirmId = '';
  /** Draft id of the current clip/text, once it has been saved as a draft. */
  @state() private draftId = '';
  @state() private draftKind?: DraftKind;
  /** Why the current clip/text is waiting (undefined: for a connection). */
  @state() private draftReason?: DraftBlock;
  /** A capture that should have become a draft but could not be kept (full, storage): still here. */
  @state() private unsaved?: {
    capture: Capture;
    who: Identity | null;
    opts: DraftSaveOpts;
    message: string;
  };
  @state() private online = typeof navigator === 'undefined' || navigator.onLine !== false;
  /** Beta: new moments left today. Undefined until loaded; `limit: null` in personal mode. */
  @state() private allowance?: Allowance;
  @state() private allocationChange = '';
  private allocationOwner: string | null = null;
  private allowanceRead = 0;
  /** The last 409 `daily-review` refusal, shown until the allowance is re-read (ticket 09). */
  @state() private refusedGate?: ReviewGate;
  /**
   * The wait (v1 ticket 41): the capture whose analysis is running, shown in place of the form only while
   * its request is in flight. A capture kept as a draft without a request never shows it.
   */
  @state() private waiting?: WaitCapture;
  /**
   * Say it instead: the note spoken, written down by the server and put in the textarea to edit. Its own recorder
   * and state, so the Record tab's are never touched; the clip lives only until the reply.
   */
  @state() private sayPhase: 'idle' | 'recording' | 'writing' = 'idle';
  @state() private sayLevel = 0;
  @state() private sayElapsed = 0;
  /** One plain line under the button when saying did not work (no microphone, a refusal, no words). */
  @state() private sayError = '';
  @state() private storageBlocker?: ResearchReason;
  /** The note just took spoken words: it glows once as they land. */
  @state() private inked = false;
  private sayRecorder?: ClipRecorder;
  private sayTick = 0;
  /** Bumped by Cancel and on leaving, so a late reply is dropped. */
  private sayRun = 0;
  private unsubDrafts?: () => void;
  private unsubSession?: () => void;
  private recorder = new ClipRecorder((l) => (this.level = l), (s) => (this.paused = s === 'paused'));
  /** `capturedAt`: when recording started — a draft's age and the recording's retention count from it. */
  private clip?: { blob: Blob; durationSec: number; capturedAt: number };
  private startedAt = 0;
  private tick = 0;
  private previewUrl = '';
  private recordingAttempt: ResearchAttempt | null = null;
  private capabilityBlocker?: ResearchReason;

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    if (this.opensOn === 'shared') void this.openShared();
    else if (this.opensOn) this.tab = this.opensOn;
    if (this.opensOn === 'wanted') this.notice = takeQuillLine();
    // A stuck mission note becomes the next lesson's starting point.
    const pending = localStorage.getItem('pendingMissionNote') || '';
    if (pending && !this.wanted) {
      this.wanted = pending;
      this.tab = 'wanted';
    }
    // Deep link from Home ("write what you wanted to say").
    if (location.hash === '#wanted') this.tab = 'wanted';
    window.addEventListener('hashchange', this.onHash);
    this.unsubDrafts = subscribeDrafts((v) => {
      this.drafts = v;
    });
    this.unsubSession = onSession((s) => {
      this.syncAllocationOwner();
      if (s.status === 'known' && !s.provisional) void this.loadAllowance();
    });
    window.addEventListener('drafts-sent', this.onDraftsSent);
    window.addEventListener('online', this.onConnection);
    window.addEventListener('offline', this.onConnection);
    window.addEventListener('focus', this.onEligibilityWake);
    document.addEventListener('visibilitychange', this.onEligibilityWake);
    void this.loadAllowance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.tab === 'wanted') cancelWrittenAttempt();
    clearInterval(this.tick);
    if (this.phase === 'recording') this.recorder.cancel();
    this.cancelSay();
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.unsubDrafts?.();
    this.unsubSession?.();
    window.removeEventListener('hashchange', this.onHash);
    window.removeEventListener('drafts-sent', this.onDraftsSent);
    window.removeEventListener('online', this.onConnection);
    window.removeEventListener('offline', this.onConnection);
    window.removeEventListener('focus', this.onEligibilityWake);
    document.removeEventListener('visibilitychange', this.onEligibilityWake);
  }

  /** Reads what was shared, measures it and applies the rules the server applies again; the clip then waits in review. */
  private async openShared() {
    const got = await readShared();
    if (!got) { this.shared = 'none'; return; }
    if ('refused' in got) { this.shared = 'too-long'; this.sharedSec = null; return; }
    const mime = sniffAudioMime(new Uint8Array(await got.blob.slice(0, 16).arrayBuffer())) || null;
    const small = got.blob.size <= SHARED_MAX_BYTES;
    let seconds = small ? oggOpusSeconds(new Uint8Array(await got.blob.arrayBuffer())) : null;
    if (seconds === null && mime && small) seconds = await measureClip(got.blob);
    const why = sharedRefusal({ bytes: got.blob.size, mime, seconds });
    if (why || !mime) {
      this.shared = why === 'too-long' ? 'too-long' : 'unsupported';
      this.sharedSec = seconds;
      return;
    }
    // The sheet closed while this was measured: no object URL nobody would revoke.
    if (!this.isConnected) return;
    const blob = new Blob([got.blob], { type: mime });
    this.clip = { blob, durationSec: seconds ?? 0, capturedAt: got.at };
    this.previewUrl = URL.createObjectURL(blob);
    this.phase = 'review';
    this.shared = 'ready';
  }

  private onConnection = () => {
    this.online = navigator.onLine !== false;
    if (this.online) void this.loadAllowance();
  };

  /** A tab can sit open across a reset or allowance change; refresh without resetting its in-progress note. */
  private onEligibilityWake = () => {
    if (document.visibilityState !== 'hidden') void this.loadAllowance();
  };

  /** How many new moments are left today; a failed read just hides the line. */
  private loadAllowance = async () => {
    const read = ++this.allowanceRead;
    const owner = currentOwner();
    this.syncAllocationOwner();
    try {
      const allowance = await api.allowance();
      if (read !== this.allowanceRead || owner !== currentOwner()) return;
      const nextAi = aiOf(allowance);
      const previousMonth = aiOf(this.allowance)?.month;
      const change = observeAllocationChange(nextAi);
      if (previousMonth && nextAi?.month !== previousMonth) this.allocationChange = '';
      if (change) this.allocationChange = change;
      this.refusedGate = undefined;
      this.allowance = allowance;
    } catch { /* the limit is enforced on the server either way */ }
  };

  private syncAllocationOwner() {
    const owner = currentOwner();
    if (owner === this.allocationOwner) return;
    this.allocationOwner = owner;
    this.allocationChange = '';
  }

  /**
   * Today's review requirement (ticket 09). It never disables the recorder:
   * only the analysis request is refused, and the clip waits on this device.
   */
  private get gate(): ReviewGate | undefined {
    return this.refusedGate ?? gateOf(this.allowance);
  }

  /**
   * True once no new moment can be created: either today's slots are gone or
   * this month's AI allowance is, and analysing a recording costs money.
   */
  private get exhausted(): boolean {
    return (!!this.allowance && this.allowance.limit !== null && this.allowance.remaining <= 0) || aiOut(this.allowance);
  }

  /**
   * What would stop a new capture becoming a lesson right now. The recorder
   * never closes (ticket 12): the capture is kept as a draft instead, without
   * a request. It can be sent after the reason lifts while the app is open.
   */
  private get blocked(): DraftBlock | null {
    return blockOf(this.allowance);
  }

  /** A new capture would become a draft rather than a lesson right now. */
  private get draftOnly(): boolean {
    return !!this.blocked || !this.online;
  }

  /**
   * Remaining slots, and once they are gone the reason plus the reset time in
   * the learner's own timezone. Only new material is blocked — practice and
   * review stay one tap away.
   */
  private renderAllowance() {
    const a = this.allowance;
    if (!a || a.limit === null) return nothing;
    return html`
      <div class="card allowance" role="status" style="margin-bottom:10px">
        <div class="allowance-count"><strong>${t('record.allowanceLeft', { remaining: a.remaining, limit: a.limit })}</strong></div>
        ${a.remaining <= 0
          ? html`
            <div class="allowance-reason muted small" style="margin-top:4px">
              ${t('record.allowanceReason', { time: resetTime(a.resetsAt, a.timezone), timezone: a.timezone })}
            </div>`
          : nothing}
        ${aiOf(a)
          ? aiOut(a)
            ? html`<div class="ai-allowance-reason muted small" style="margin-top:4px">
                ${t('ai.recordReason', { reason: aiReason(aiOf(a)!) })}
              </div>`
            : html`<div class="ai-allowance-note muted small" style="margin-top:4px">${aiLine(aiOf(a)!)}</div>`
          : nothing}
        ${aiOf(a) ? html`<div class="ai-scope muted small">${t('ai.scope')}</div>` : nothing}
        ${this.allocationChange ? html`<div class="ai-allocation-change muted small" role="status">${this.allocationChange}</div>` : nothing}
        ${this.exhausted
          ? html`<button class="btn block" style="margin-top:8px" @click=${() => go('/')}>${t('record.practiseInstead')}</button>`
          : nothing}
      </div>
    `;
  }

  /** The Type tab answers a newcomer's question (its question was read). */
  private get answering(): boolean {
    return this.tab === 'wanted' && !!this.answer && !!this.question;
  }

  /**
   * The allowance while answering a newcomer (the revamp): the day's counters and the AI line as one muted line at the
   * foot, under the button, so the question comes first. Out of moments, its reason and Practise instead stay.
   */
  private renderAllowanceLine() {
    const a = this.allowance;
    if (!a || a.limit === null) return nothing;
    const ai = aiOf(a);
    const parts = [
      t('record.allowanceLeft', { remaining: a.remaining, limit: a.limit }),
      ...(a.remaining <= 0 ? [t('record.allowanceReason', { time: resetTime(a.resetsAt, a.timezone), timezone: a.timezone })] : []),
      ...(ai ? [aiOut(a) ? t('ai.recordReason', { reason: aiReason(ai) }) : aiLine(ai)] : []),
    ];
    return html`<div class="answer-allowance muted small" role="status">${parts.join(t('common.dot'))}</div>
      ${ai ? html`<div class="ai-scope muted small">${t('ai.scope')}</div>` : nothing}
      ${this.allocationChange ? html`<div class="ai-allocation-change muted small" role="status">${this.allocationChange}</div>` : nothing}
      ${this.exhausted ? html`<button class="btn block answer-practise" @click=${() => go('/')}>${t('record.practiseInstead')}</button>` : nothing}`;
  }

  /** New text is a new capture: the last one's "saved"/"couldn't keep" lines no longer apply. */
  private onWantedInput = (e: Event) => {
    this.wanted = (e.target as HTMLTextAreaElement).value;
    this.sayError = '';
    if (this.unsaved?.capture.kind === 'text') this.unsaved = undefined;
    if (this.draftKind === 'text') {
      this.draftId = '';
      this.draftKind = undefined;
      this.draftReason = undefined;
    }
  };

  /** If the view is already open, a later navigation to /record#wanted still switches tabs. */
  private onHash = () => {
    if (location.hash === '#wanted') this.tab = 'wanted';
  };

  protected willUpdate(changed: Map<string, unknown>) {
    if (changed.has('answer')) void this.loadQuestion();
  }

  /** Reads the answered moment's question; a moment gone or with none leaves a plain Type tab. */
  private async loadQuestion() {
    const id = this.answer;
    this.question = undefined;
    this.asker = null;
    if (!id) return;
    const m = await api.getMoment(id).catch(() => null);
    if (this.answer !== id) return;
    this.question = questionOf(m);
    this.asker = personOf(m);
  }

  /**
   * The newcomer's question over the note: the line (Finglish first), its translation under. The title already says
   * who asked when the moment names them; an older one keeps "Marta asked:".
   */
  private renderQuestion() {
    const q = this.question;
    if (!this.answer || !q) return nothing;
    return html`<div class="answer-ask" data-answer=${this.answer}>
      ${this.asker ? nothing : html`<div class="muted small">${tPart('record.answerAsked', 'name', html`<bdi dir="auto">${q.name}</bdi>`)}</div>`}
      ${q.latin
        ? html`<bdi class="ca answer-q" dir="auto">${q.latin}</bdi><bdi class="muted small answer-script" dir="auto">${q.ca}</bdi>`
        : html`<bdi class="ca answer-q" dir="auto">${q.ca}</bdi>`}
      <div class="answer-tr muted small" dir="auto">${q.en}</div>
    </div>`;
  }

  /** The tab shows on the element, so home's panel takes its look (dark for Record, parchment for Type). */
  protected updated(changed: Map<PropertyKey, unknown>) {
    // The shared-clip refusal panel can offer the ordinary capture routes in
    // this same sheet. Keep the form's tab in step with the route it receives.
    if (changed.has('opensOn') && this.opensOn && this.opensOn !== 'shared') this.tab = this.opensOn;
    if (this.dataset.tab !== this.tab) this.dispatchEvent(new CustomEvent('capture-tab', { detail: this.tab }));
    this.dataset.tab = this.tab;
    if (this.tab === 'wanted' && this.querySelector('.wanted-go button')) prepareWrittenAttempt();
    else if (this.tab === 'record' && !this.sent && !this.waiting && this.phase !== 'uploading' && !this.draftId) {
      this.recordingAttempt = prepareRecordedAttempt();
    }
    if (this.capabilityBlocker) recordDisplayedBlocker(this.capabilityBlocker, this.recordingAttempt);
    const reason = this.researchReasonForBlock(this.blocked?.reason) ?? (this.refusedGate ? 'daily_review' : undefined);
    if (reason) recordDisplayedBlocker(reason, this.recordingAttempt ?? researchAttemptFor('recorded'));
    if (this.storageBlocker) recordDisplayedBlocker(this.storageBlocker, this.recordingAttempt ?? researchAttemptFor('recorded'));
  }

  /** Back on home's panel: the panel closes, as its ✕ does. */
  private dismiss = () => {
    this.dispatchEvent(new CustomEvent('capture-dismiss', { bubbles: true }));
  };

  render() {
    const mm = String(Math.floor(this.elapsed / 60)).padStart(1, '0');
    const ss = String(Math.floor(this.elapsed % 60)).padStart(2, '0');
    return html`
      ${this.waiting ? html`<moment-wait .capture=${this.waiting}></moment-wait>` : this.renderForm(mm, ss)}

      ${this.renderDrafts()}
      ${this.notice ? html`<div class="toast">${this.notice}</div>` : nothing}
      ${this.error ? html`<div class="toast error">${this.error}</div>` : nothing}
    `;
  }

  /** The tabs, the allowance and the capture in hand: everything the wait stands in for. */
  private renderForm(mm: string, ss: string) {
    if (this.opensOn === 'shared') return this.renderShared();
    return html`
        ${oneButtonOn() && this.opensOn === 'wanted' ? nothing : html`<div style="display:flex;gap:8px;margin-bottom:10px">
          <button class="btn ${this.tab === 'record' ? 'primary' : ''}" style="flex:1" @click=${() => { this.cancelSay(); if (this.tab === 'wanted') cancelWrittenAttempt(); this.tab = 'record'; }}>${t('record.tabRecord')}</button>
          <button class="btn ${this.tab === 'wanted' ? 'primary' : ''}" style="flex:1" @click=${() => (this.tab = 'wanted')}>${t('record.tabWanted')}</button>
        </div>`}

        ${this.answering ? nothing : this.renderAllowance()}
        ${renderGate(this.gate, { hint: true, dueCount: this.allowance?.limit === null ? undefined : this.allowance?.dueCount, roundCards: this.allowance?.limit === null ? undefined : this.allowance?.round?.cards, timezone: tzOf(this.allowance) })}

        ${this.tab === 'wanted' ? this.renderWanted() : ''}

        ${this.tab === 'record'
          ? this.phase === 'review' || this.phase === 'uploading'
            ? this.renderReview()
            : html`
              <div class="record-stage">
                ${this.opensOn && this.phase === 'recording'
                  ? html`<div class="fnc-rec-head"><span class="fnc-dot" aria-hidden="true"></span><span>${t('record.listening')}</span>
                      <span class="timer">${num(mm)}:${num(ss)}</span></div>`
                  : html`<div class="timer">${num(mm)}:${num(ss)}</div>`}
                <div class="level"><div style="width:${Math.round(this.level * 100)}%"></div></div>
                <button
                  class="rec-button ${this.phase === 'recording' ? (this.paused ? 'paused' : 'recording') : ''} ${this.opensOn && this.phase === 'recording' ? 'fnc-stop' : ''}"
                  ?disabled=${this.phase !== 'recording' && this.drafts.readError}
                  @click=${this.phase === 'recording' ? this.stop : this.start}
                >
                  ${this.phase === 'recording' ? t('record.stop') : t('record.rec')}
                </button>
                <div class="muted small" style="text-align:center;max-width:300px;min-height:36px">
                  ${this.phase === 'recording'
                    ? this.paused
                      ? html`<span class="pill">${t('record.paused')}</span>`
                      : `${t('record.recording', { max: MAX_SEC })}${this.skipped >= 1 ? t('record.skippedQuiet', { n: Math.round(this.skipped) }) : ''}`
                    : t('record.idleHint')}
                </div>
                <label class="muted small" style="display:flex;align-items:center;gap:8px">
                  <input type="checkbox" .checked=${this.skipSilence} @change=${(e: Event) => { this.skipSilence = (e.target as HTMLInputElement).checked; this.recorder.skipSilence = this.skipSilence; }} />
                  ${t('record.skipSilence')}
                </label>
              </div>
            `
          : nothing}
    `;
  }

  /** Share to Eaves: the voice message in the review step (tabs hidden), or the one line saying why there is none. */
  private renderShared() {
    const s = this.shared;
    if (s === 'loading') return nothing;
    // OS share-target support has no browser feature probe. Explain the supported
    // path conservatively; a file already received remains usable on any platform.
    const platform = isIos(navigator.userAgent, navigator.maxTouchPoints) ? 'ios'
      : /Android/.test(navigator.userAgent) ? 'android' : 'other';
    const capabilities = html`<p class="shared-capabilities muted small" role="status">${t(`shared.capability.${platform}`)}</p>
      ${!this.online ? html`<p class="shared-offline" role="status">${t('shared.offline')}</p>` : nothing}`;
    if (s !== 'ready') {
      // Rounded up: a 30.6 s clip must not read 0:30 beside "up to 30 seconds".
      const whole = this.sharedSec === null ? null : Math.ceil(this.sharedSec);
      const mmss = whole === null ? '' : `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
      return html`
        ${capabilities}
        <div class="card shared-note" role="status">
          ${s === 'none' ? t('shared.none')
            : s === 'unsupported' ? t('shared.unsupported')
            : mmss ? tPart('shared.tooLong', 'length', html`<bdi dir="ltr">${num(mmss)}</bdi>`) : t('error.shared-too-long')}
        </div>
        <button class="btn block shared-record" @click=${() => this.useSharedAlternative('record')}>${t('shared.recordInstead')}</button>
        <button class="btn block shared-type" @click=${() => this.useSharedAlternative('wanted')}>${t('shared.typeInstead')}</button>
        <button class="btn block shared-close" @click=${this.dismiss}>${t('tend.close')}</button>`;
    }
    return html`
      ${capabilities}
      <div class="card shared-head">
        <h2>${t('shared.title')}</h2>
        <div class="muted small">${t('shared.sub')}</div>
      </div>
      ${this.renderAllowance()}
      ${renderGate(this.gate, { hint: true, dueCount: this.allowance?.limit === null ? undefined : this.allowance?.dueCount, roundCards: this.allowance?.limit === null ? undefined : this.allowance?.round?.cards, timezone: tzOf(this.allowance) })}
      ${this.renderReview()}`;
  }

  /** Cancel on a shared message: the stash goes and the panel closes, with nothing to ask about. */
  private cancelShared = () => {
    void dropShared();
    this.dismiss();
  };

  /** Leave the share review deliberately; the unsupported clip is dropped only after this choice. */
  private useSharedAlternative = (tab: CaptureTab) => {
    void dropShared();
    this.dispatchEvent(new CustomEvent('capture-alternative', { detail: tab, bubbles: true, composed: true }));
  };

  private renderWanted() {
    const uploading = this.phase === 'uploading';
    return html`
      <div class="card">
        ${this.answering && this.asker
          ? html`<h2 class="answer-title">${tPart('record.answerTitleNamed', 'name', nameHtml(this.asker, true))}</h2>
              ${this.asker.role ? html`<div class="muted small answer-role" dir="auto">${this.asker.role}</div>` : nothing}`
          : html`<h2>${this.answering ? t('record.answerTitle') : t('record.wantedTitle')}</h2>`}
        ${this.renderQuestion()}
        <div class="muted small" style="margin-bottom:8px">${this.answer && this.question ? t('record.answerHint') : t('record.wantedHint')}</div>
        <div class="note-box ${this.sayPhase === 'writing' ? `writing ${this.wanted.trim() ? 'note-more' : 'note-empty'}` : ''} ${this.inked ? 'inked' : ''}"
          @animationend=${(e: AnimationEvent) => { if (e.animationName === 'note-inked') this.inked = false; }}>
          <textarea
            class="note"
            placeholder=${this.sayPhase === 'writing' ? '' : this.answer && this.question ? t('record.answerPlaceholder') : tOr(`record.wantedPlaceholder.${currentTarget()}`, t('record.wantedPlaceholder'))}
            .value=${this.wanted}
            ?disabled=${uploading}
            ?readonly=${this.sayPhase === 'writing'}
            @input=${this.onWantedInput}
          ></textarea>
          ${this.sayPhase === 'writing'
            // The words are drawn where they will land: faint lines inked into the note itself.
            ? html`<div class="note-ink wait-late" aria-hidden="true">${skeletonLine(0.92)}${skeletonLine(0.64)}</div>`
            : nothing}
        </div>
        ${this.renderSay(uploading)}
      </div>
      <div class="wanted-go">
      ${this.opensOn ? html`<button class="btn fnc-sec" ?disabled=${uploading} @click=${this.dismiss}>${t('common.back')}</button>` : nothing}
      ${this.unsaved?.capture.kind === 'text'
        ? html`<button class="btn primary block save-draft" style="min-height:56px" @click=${this.saveUnsaved}>${t('drafts.saveAsDraft')}</button>`
        : html`<button class="btn primary block" style="min-height:56px" ?disabled=${uploading || this.sayPhase !== 'idle' || this.wanted.trim().length < 3} @click=${this.uploadWanted}>
            ${uploading ? html`<span class="spinner"></span> ${t('record.turning')}`
              : this.draftOnly ? t('drafts.saveAsDraft')
                : this.answering && this.asker ? answerLabel(this.asker, 'record.answerSend') : t('record.makeScene')}
          </button>`}
      </div>
      ${this.draftOnly || this.unsaved || this.answering ? nothing : html`<div class="muted small capture-save-note">${t('record.saveNote')}</div>`}
      ${this.answering ? this.renderAllowanceLine() : nothing}
      ${this.renderDraftCount('text')}
      ${this.draftId && this.draftKind === 'text' ? this.renderSaved('text') : nothing}
    `;
  }

  /** Say it instead: the button, the recording (the Record tab's head and meter), or the wait while it is written. */
  private renderSay(uploading: boolean) {
    if (this.sayPhase === 'recording') {
      const mm = String(Math.floor(this.sayElapsed / 60));
      const ss = String(Math.floor(this.sayElapsed % 60)).padStart(2, '0');
      return html`<div class="say-note recording">
        <div class="fnc-rec-head"><span class="fnc-dot" aria-hidden="true"></span><span>${t('record.listening')}</span>
          <span class="timer">${num(mm)}:${num(ss)}</span></div>
        <div class="level"><div style="width:${Math.round(this.sayLevel * 100)}%"></div></div>
        <div class="say-note-go">
          <button class="btn fnc-sec say-cancel" @click=${this.cancelSay}>${t('record.sayCancel')}</button>
          <button class="btn primary say-stop" @click=${this.stopSay}>${t('record.sayStop')}</button>
        </div>
      </div>`;
    }
    if (this.sayPhase === 'writing') {
      return html`<div class="say-note writing" aria-busy="true">
        <wait-caption .lines=${[t('record.sayWriting')]}></wait-caption>
        <div class="say-note-go"><button class="btn fnc-sec say-cancel" @click=${this.cancelSay}>${t('record.sayCancel')}</button></div>
      </div>`;
    }
    return html`<div class="say-note">
      <button class="btn say-start" ?disabled=${uploading} @click=${this.startSay}>
        ${MIC_ICON}${this.wanted.trim() ? t('record.sayMore') : t('record.sayIt')}
      </button>
      ${this.sayError ? html`<div class="say-error small" role="status">${this.sayError}</div>` : nothing}
    </div>`;
  }

  /** Start saying the note: no microphone (none here, an insecure page, refused) leaves one line and the typing. */
  private startSay = async () => {
    this.sayError = '';
    const drafts = await refreshDrafts();
    if (!this.isConnected) return;
    if (drafts.readError) {
      this.sayError = t('drafts.readError');
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      this.sayError = t('record.sayNoMic');
      return;
    }
    const run = ++this.sayRun;
    const rec = new ClipRecorder((l) => { if (this.sayRecorder === rec) this.sayLevel = l; });
    // A note may pause to think: every second of it is kept.
    rec.skipSilence = false;
    this.sayRecorder = rec;
    try {
      await rec.start();
    } catch (e) {
      if (this.sayRecorder === rec) this.sayRecorder = undefined;
      rec.cancel();
      if (run !== this.sayRun) return;
      const name = e instanceof Error ? e.name : '';
      this.sayError = name === 'NotAllowedError' || name === 'SecurityError' ? t('record.sayMicDenied') : t('record.sayNoMic');
      return;
    }
    if (run !== this.sayRun) { rec.cancel(); return; }
    this.sayPhase = 'recording';
    this.sayElapsed = 0;
    this.sayTick = window.setInterval(() => {
      this.sayElapsed = rec.elapsedSec;
      if (this.sayElapsed >= MAX_SEC) void this.stopSay();
    }, 200);
  };

  /** Cancel: the recording or the wait goes, and nothing lands in the textarea. */
  private cancelSay = () => {
    this.sayRun++;
    clearInterval(this.sayTick);
    this.sayRecorder?.cancel();
    this.sayRecorder = undefined;
    this.sayPhase = 'idle';
    this.sayLevel = 0;
  };

  /** Stop, send the clip once to be written down, and put the words at the end of the note. */
  private stopSay = async () => {
    clearInterval(this.sayTick);
    const rec = this.sayRecorder;
    if (this.sayPhase !== 'recording' || !rec) return;
    const run = this.sayRun;
    this.sayRecorder = undefined;
    this.sayPhase = 'writing';
    let text = '';
    try {
      // The clip is held only here: never a draft, never sent again, gone with this call.
      const { blob } = await rec.stop();
      if (run !== this.sayRun) return;
      text = (await api.transcribeNote(blob)).text.trim();
    } catch (e) {
      if (run !== this.sayRun) return;
      this.sayPhase = 'idle';
      this.sayError = errorText(e);
      return;
    }
    if (run !== this.sayRun) return;
    this.sayPhase = 'idle';
    if (!text) {
      this.sayError = t('record.sayEmpty');
      return;
    }
    const before = this.wanted.replace(/\s+$/, '');
    this.wanted = before ? `${before}${/\n/.test(before) ? '\n' : ' '}${text}` : text;
    this.inked = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    // New words are a new capture, as typing them would be.
    if (this.unsaved?.capture.kind === 'text') this.unsaved = undefined;
    if (this.draftKind === 'text') {
      this.draftId = '';
      this.draftKind = undefined;
      this.draftReason = undefined;
    }
    await this.updateComplete;
    const area = this.querySelector<HTMLTextAreaElement>('textarea.note');
    if (area) {
      area.focus();
      area.setSelectionRange(area.value.length, area.value.length);
    }
  };

  private renderReview() {
    const uploading = this.phase === 'uploading';
    const shared = this.opensOn === 'shared';
    return html`
      <div class="card">
        ${shared ? nothing : html`<h2>${t('record.recordingTitle')}</h2>`}
        <div style="display:flex;align-items:center;gap:12px">
          <audio controls src=${this.previewUrl} style="flex:1;max-width:100%"></audio>
          ${this.clip?.durationSec ? html`<span class="muted small">${t('record.seconds', { n: Math.round(this.clip.durationSec) })}</span>` : nothing}
        </div>
      </div>
      <div class="card">
        <h2>${t('record.momentTitle')} <span class="muted" style="text-transform:none;font-weight:400">${t('record.optional')}</span></h2>
        <textarea
          class="note"
          placeholder=${t('record.notePlaceholder')}
          .value=${this.note}
          ?disabled=${uploading}
          @input=${(e: Event) => (this.note = (e.target as HTMLTextAreaElement).value)}
        ></textarea>
        <div class="muted small" style="margin-top:8px">${t('record.noteHint')}</div>
      </div>
      ${this.draftId && this.draftKind === 'audio'
        ? this.renderSaved('audio')
        : this.unsaved?.capture.kind === 'audio'
          ? html`<button class="btn primary block save-draft" style="min-height:56px" @click=${this.saveUnsaved}>${t('drafts.saveAsDraft')}</button>`
          : html`<button class="btn primary block" style="min-height:56px" ?disabled=${uploading} @click=${this.upload}>
              ${uploading ? html`<span class="spinner"></span> ${t('record.analysing')}` : this.draftOnly ? t('drafts.saveAsDraft') : t('record.turnIntoLesson')}
            </button>`}
      ${this.draftOnly || this.unsaved || (this.draftId && this.draftKind === 'audio') ? nothing : html`<div class="muted small capture-save-note">${t('record.saveNote')}</div>`}
      ${this.draftId && this.draftKind === 'audio' ? nothing : this.renderDraftCount('audio')}
      ${shared
        ? html`<button class="btn ghost block shared-cancel" ?disabled=${uploading} @click=${this.cancelShared}>${t('common.cancel')}</button>`
        : html`<button class="btn ghost block" ?disabled=${uploading} @click=${this.discard}>${t('record.recordAgain')}</button>`}
    `;
  }

  private start = async () => {
    this.error = '';
    this.notice = '';
    this.capabilityBlocker = undefined;
    this.storageBlocker = undefined;
    this.recordingAttempt = prepareRecordedAttempt();
    const drafts = await refreshDrafts();
    if (drafts.readError) return;
    // A previous clip may still wait below as a draft — the "saved" line
    // belongs to it, not to the new recording.
    this.forgetCapture();
    try {
      await this.recorder.start();
    } catch (e) {
      this.error = `${t('record.micNeeded')} ${browserDetail(e)}`.trim();
      const name = e instanceof Error ? e.name : '';
      const reason: ResearchReason = name === 'NotAllowedError' || name === 'SecurityError'
        ? 'permission_denied'
        : name === 'NotFoundError' || name === 'NotReadableError' ? 'microphone_unavailable' : 'capture_failed';
      this.capabilityBlocker = reason;
      recordCaptureEvent('capture.permission_failed', reason, this.recordingAttempt);
      return;
    }
    recordCaptureEvent('capture.started', undefined, this.recordingAttempt);
    this.phase = 'recording';
    this.startedAt = Date.now();
    this.elapsed = 0;
    this.skipped = 0;
    this.paused = false;
    this.recorder.skipSilence = this.skipSilence;
    this.tick = window.setInterval(() => {
      this.elapsed = this.recorder.elapsedSec;
      this.skipped = this.recorder.skippedSec;
      if (this.elapsed >= MAX_SEC) this.stop();
    }, 200);
  };

  private stop = async () => {
    clearInterval(this.tick);
    if (this.phase !== 'recording') return;
    this.clip = { ...(await this.recorder.stop()), capturedAt: this.startedAt || Date.now() };
    recordCaptureEvent('capture.stopped', undefined, this.recordingAttempt);
    this.paused = false;
    // One local playback URL at a time: the previous clip's is let go.
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = URL.createObjectURL(this.clip.blob);
    this.phase = 'review';
  };

  private discard = () => {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = '';
    this.clip = undefined;
    this.elapsed = 0;
    this.forgetCapture();
    this.phase = 'idle';
    cancelCaptureAttempt('recorded');
    this.recordingAttempt = null;
  };

  /** The save went through and the form is on its way to the landing: nothing here is at stake any more. */
  private sent = false;

  /**
   * Something only this form holds would go if it went now: a recording under way, a clip not yet sent or kept as a
   * draft, or one being sent (its draft is kept only while the form is up). The capture panel asks before closing.
   * A shared message is still in the chat app it came from: only its sending is at stake.
   */
  atStake(): boolean {
    if (this.sent) return false;
    if (this.opensOn === 'shared') return this.phase === 'uploading' || !!this.unsaved;
    return this.phase === 'recording' || this.phase === 'uploading' || this.sayPhase !== 'idle'
      || (this.phase === 'review' && !!this.clip && !this.draftId)
      || this.wanted.trim().length > 0;
  }

  private forgetCapture() {
    this.draftId = '';
    this.draftKind = undefined;
    this.draftReason = undefined;
    this.unsaved = undefined;
  }

  private upload = async () => {
    if (!this.clip) return;
    // One logical request id per capture: a retry after a lost response
    // resumes the same server-side moment instead of creating a duplicate.
    const capture: Sent = {
      clientId: makeClientId(),
      kind: 'audio',
      blob: this.clip.blob,
      note: this.note.trim() || undefined,
      durationSec: this.clip.durationSec || undefined,
      capturedAt: this.clip.capturedAt,
      researchAttempt: this.recordingAttempt ?? researchAttemptFor('recorded') ?? undefined,
      ...(this.opensOn === 'shared' ? { extra: { shared: true } } : {}),
    };
    // Keep the received file until a lesson or durable draft takes over. Storage
    // can fail while offline; the in-memory preview alone is not a safe hand-off.
    this.phase = 'uploading';
    await this.submit(capture);
    if (this.phase === 'uploading') this.phase = 'review';
  };

  private uploadWanted = async () => {
    const text = this.wanted.trim();
    if (text.length < 3) return;
    this.phase = 'uploading';
    // Sent whenever the route names a moment, even before (or without) its question loading: the server checks it and
    // ignores one that is not a newcomer moment. Closing the panel clears the route, so a later note never carries it.
    const answer = this.answer || undefined;
    await this.submit({ clientId: makeClientId(), kind: 'text', wanted: text, ...(answer ? { answer } : {}) });
    if (this.phase === 'uploading') this.phase = 'idle';
  };

  /** Who the capture belongs to: the signed-in learner, once the server (or, offline, this device) knows. */
  private async identity(): Promise<Identity | null> {
    let s = session();
    if (s.status === 'unknown') s = await resolveSession();
    return s.status === 'known' ? s : null;
  }

  /**
   * Turn a capture into a lesson now, or keep it as a draft when it can't be
   * one yet. A block the allowance already shows, or no connection, saves it
   * straight away without a request.
   */
  private async submit(capture: Sent) {
    this.error = '';
    this.notice = '';
    const who = await this.identity();
    const predicted = this.blocked;
    if (predicted) {
      await this.keep(capture, who, { blocked: predicted });
      return;
    }
    if (!this.online) {
      await this.keep(capture, who, {});
      return;
    }
    // The wait shows the learner's own clip or note while the analysis runs (ticket 41).
    this.waiting = capture.kind === 'audio'
      ? { kind: 'audio', blob: capture.blob, ...(capture.extra?.shared ? { shared: true } : {}) }
      : { kind: 'text', text: capture.wanted };
    try {
      const m = await sendCapture(capture, who);
      if (capture.extra?.shared) await dropShared();
      if (capture.kind === 'text') localStorage.removeItem('pendingMissionNote');
      this.forgetCapture();
      // Every new moment lands in the town first (ticket 41). The wait stays up until this view goes:
      // bringing the form back here would reload its preview from a URL the view is about to let go. A form closed
      // meanwhile (the panel shut) leaves it to home's next open, which lands every moment not landed yet.
      this.sent = true;
      expectLessonDisplay(m.id, capture.researchAttempt);
      if (this.isConnected) go(`/land/${m.id}`, true);
    } catch (e) {
      this.waiting = undefined;
      // A refusal may have been the allowance running out — re-read it.
      void this.loadAllowance();
      const o = outcomeOf(e);
      const audio = capture.kind === 'audio';
      if (o.kind === 'blocked') {
        const refused = gateRefusal(e);
        if (refused) this.refusedGate = { status: 'pending', ...refused, resetsAt: '' };
        await this.keep(capture, who, { blocked: { reason: o.reason, resetsAt: o.resetsAt } });
      } else if (o.kind === 'offline' || o.kind === 'transient') {
        const offline = o.kind === 'offline';
        // A transport/server error does not prove creation failed: the server
        // may have committed the moment before its answer was lost. Keep that
        // uncertainty with the stable id so the next send reconciles first.
        const saved = await this.keep(capture, who, { lastError: o.message, ambiguous: true });
        this.error = saved
          ? t(audio ? (offline ? 'record.errOfflineAudio' : 'record.errServerAudio') : offline ? 'record.errOfflineText' : 'record.errServerText')
          : t(audio ? (offline ? 'record.errOfflineAudioNoSave' : 'record.errServerAudioNoSave') : offline ? 'record.errOfflineTextNoSave' : 'record.errServerTextNoSave');
      } else if (o.kind === 'session') {
        // Signed out meanwhile: kept for the account it was made under, hidden until it signs in again.
        await this.keep(capture, who, {});
      } else if (o.kind === 'failed' && (o.reason === 'draft-account' || o.reason === 'draft-language')) {
        // Somebody else signed in meanwhile (another tab): nothing was made and
        // nothing is kept under the wrong account — the capture stays here.
        await resolveSession();
        this.error = t(o.reason === 'draft-account' ? 'drafts.whyAccount' : 'drafts.whyLanguage');
      } else if (o.kind === 'failed' && o.reason === 'too-large' && !audio) {
        this.error = t('drafts.tooLong', { max: MAX_TEXT_CHARS });
      } else {
        this.error = errorText(e);
      }
    }
  }

  /**
   * Keep a capture as a draft for its owner. When that can't be done (the
   * device already holds the maximum, storage failed) the capture stays on
   * this screen with the reason, and "Save as a draft" tries again.
   */
  private async keep(
    capture: Capture,
    who: Identity | null,
    opts: DraftSaveOpts,
  ): Promise<boolean> {
    try {
      await saveDraft(capture, who, opts);
    } catch (e) {
      this.unsaved = { capture, who, opts, message: this.saveProblem(e) };
      if (capture.kind === 'audio') this.storageBlocker = this.researchStorageReason(e);
      return false;
    }
    this.unsaved = undefined;
    if (capture.kind === 'audio') {
      const attempt = capture.researchAttempt ?? this.recordingAttempt ?? researchAttemptFor('recorded');
      const reason = this.researchReasonForBlock(opts.blocked?.reason);
      recordLocalDraftSaved(reason, attempt);
    }
    this.draftId = capture.clientId;
    this.draftKind = capture.kind;
    this.draftReason = opts.blocked;
    if (capture.extra?.shared) await dropShared();
    if (capture.kind === 'text') {
      this.wanted = '';
      localStorage.removeItem('pendingMissionNote');
    }
    return true;
  }

  private researchReasonForBlock(reason?: DraftBlock['reason']): ResearchReason | undefined {
    switch (reason) {
      case 'daily-review': return 'daily_review';
      case 'daily-allowance': return 'daily_allowance';
      case 'ai-allowance': return 'ai_allowance';
      case 'beta-budget': return 'beta_budget';
      default: return undefined;
    }
  }

  private researchStorageReason(error: unknown): ResearchReason {
    if (!(error instanceof DraftSaveError)) return 'storage_unavailable';
    switch (error.code) {
      case 'full': return 'storage_full';
      case 'too-large':
      case 'too-long': return 'storage_size';
      case 'no-owner': return 'storage_owner';
      default: return 'storage_unavailable';
    }
  }

  private saveUnsaved = async () => {
    const u = this.unsaved;
    if (!u) return;
    this.error = '';
    this.notice = '';
    // Nobody was known before (offline cold start)? Whoever is signed in now.
    const who = u.who ?? (await this.identity());
    await this.keep(u.capture, who, u.opts);
  };

  private saveProblem(e: unknown): string {
    const code = e instanceof DraftSaveError ? e.code : 'storage';
    switch (code) {
      case 'full': return t('drafts.full', { max: this.drafts.max });
      case 'too-long': return t('drafts.tooLong', { max: MAX_TEXT_CHARS });
      case 'too-large': return t('drafts.whyTooLarge');
      case 'no-owner': return t('drafts.noOwner');
      default: return t('drafts.cantSave', { err: e instanceof Error ? e.message : String(e ?? '') });
    }
  }

  /** The "saved as a draft" line under the capture that just became one. */
  private renderSaved(kind: DraftKind) {
    const r = this.draftReason;
    return html`<div class="muted small saved-here" role="status" style="text-align:center;margin-top:8px">
      ${savedLine(kind, r?.reason, r?.resetsAt, tzOf(this.allowance))}
    </div>`;
  }

  /**
   * "n of 5" wherever a capture would become a draft, and — when it could
   * not be kept — why, next to the button that tries again.
   */
  private renderDraftCount(kind: DraftKind) {
    const unsaved = this.unsaved?.capture.kind === kind ? this.unsaved : undefined;
    if (!unsaved && (this.drafts.readError || !this.draftOnly)) return nothing;
    return html`
      ${unsaved ? html`<div class="card draft-unsaved" role="alert" style="margin-top:8px">${unsaved.message}</div>` : nothing}
      ${this.drafts.owner && !this.drafts.readError
        ? html`<div class="muted small draft-count" style="text-align:center;margin-top:6px">${t('drafts.count', { n: this.drafts.drafts.length, max: this.drafts.max })}</div>`
        : nothing}
    `;
  }

  /** The signed-in learner's drafts on this device: state, try now, delete. Nobody else's. */
  private renderDrafts() {
    const v = this.drafts;
    if (v.readError) return html`
      <div class="card drafts draft-read-error-card" role="alert">
        <div class="error small">${t('drafts.readError')}</div>
        <button class="btn draft-read-retry" style="margin-top:8px" @click=${() => void refreshDrafts()}>${t('common.retry')}</button>
      </div>
    `;
    if (!v.owner || (v.drafts.length === 0 && v.completed.length === 0 && !v.expired)) return nothing;
    const tz = tzOf(this.allowance);
    const busy = this.busyId !== '' || !!v.sendingId;
    const sendable = v.drafts.filter((d) => !needsHearing(d) && tryable(stateOf(d, v)));
    return html`
      <div class="card drafts">
        <h2>${t('drafts.title', { n: v.drafts.length, max: v.max })}</h2>
        <div class="muted small drafts-hint" style="margin-bottom:4px">${limitsHint(v)}</div>
        ${v.expired
          ? html`<div class="drafts-expired" style="display:flex;gap:8px;align-items:center;padding:8px 0;border-top:1px solid var(--line)">
              <div class="small" style="flex:1;min-width:0">${expiredLine(v.expired)}</div>
              <button class="btn ghost draft-dismiss" aria-label=${t('drafts.dismiss')} @click=${dismissExpired}>✕</button>
            </div>`
          : nothing}
        ${v.completed.map(
          (c) => html`
            <div class="draft" data-state=${completedState(c)} data-id=${c.id}
              style="display:flex;gap:8px;align-items:center;padding:8px 0;border-top:1px solid var(--line)">
              <div style="flex:1;min-width:0">
                <div>${c.kind === 'audio' ? t('record.pendingAudio') : t('record.pendingText')}</div>
                <div class="muted small draft-state">${c.removed ? t('drafts.removed') : t('drafts.completed')}</div>
              </div>
              ${c.momentId ? html`<button class="btn draft-open" @click=${() => this.openCompleted(c)}>${t('drafts.open')}</button>` : nothing}
              <button class="btn ghost draft-dismiss" aria-label=${t('drafts.dismiss')} @click=${() => dismissCompleted(c.id)}>✕</button>
            </div>
          `,
        )}
        ${v.drafts.map((d) => this.renderDraft(d, v, tz, busy))}
        ${sendable.length > 1
          ? html`<button class="btn block draft-try-all" style="margin-top:4px" ?disabled=${busy} @click=${this.tryAll}>
              ${this.busyId === '__all__' ? html`<span class="spinner"></span> ${t('record.sending')}` : t('drafts.tryAll')}
            </button>`
          : nothing}
      </div>
    `;
  }

  private renderDraft(d: Draft, v: DraftsView, tz: string | undefined, busy: boolean) {
    const st = stateOf(d, v);
    const sending = st === 'sending';
    const action = actionFor(st);
    const text = d.note || d.wanted;
    return html`
      <div class="draft" data-state=${st} data-reason=${d.blockedReason ?? d.failReason ?? ''} data-id=${d.id}
        style="padding:8px 0;border-top:1px solid var(--line)">
        <div style="display:flex;gap:8px;align-items:center">
          <div style="flex:1;min-width:0">
            <div>
              ${[
                d.kind === 'audio' ? t('record.pendingAudio') : t('record.pendingText'),
                fmtCreated(d.createdAt),
                d.durationSec ? t('record.seconds', { n: Math.round(d.durationSec) }) : '',
                d.attempts ? (d.attempts === 1 ? t('record.tryOne') : t('record.tryMany', { n: d.attempts })) : '',
              ].filter(Boolean).join(t('common.dot'))}
            </div>
            ${text ? html`<div class="muted small draft-text" dir="auto" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${text}</div>` : nothing}
            <div class="muted small draft-state">${stateLine(d, sending, tz)}</div>
            ${needsHearing(d) ? html`<div class="muted small">${t('drafts.recordingKept')}</div>` : nothing}
            ${st === 'local' && d.lastError && !sending ? html`<div class="muted small">${t('record.lastTry', { err: storedErrorText(d.lastError) })}</div>` : nothing}
          </div>
          ${sending
            ? html`<span class="spinner"></span>`
            : html`
              ${st === 'blocked' && d.blockedReason === 'daily-review'
                ? html`<button class="btn draft-review" @click=${() => go('/round')}>${t('gate.reviewNow')}</button>`
                : nothing}
              ${needsHearing(d) ? html`<button class="btn draft-hear" ?disabled=${busy} @click=${() => go(savedRecordingPath(d.id))}>${t('drafts.resumeRecording')}</button>` : nothing}
              ${!needsHearing(d) && (action?.kind === 'send' || action?.kind === 'check')
                ? html`<button class="btn draft-try" ?disabled=${busy} @click=${() => this.tryDraft(d)}>
                    ${this.busyId === d.id ? html`<span class="spinner"></span>` : action.label}
                  </button>`
                : nothing}
              <button class="btn ghost draft-delete" ?disabled=${this.busyId === d.id} aria-label=${t('drafts.deleteAria')}
                @click=${() => (this.confirmId = d.id)}>✕</button>
            `}
        </div>
        ${this.confirmId === d.id && !sending
          ? html`
            <div class="draft-confirm" role="alertdialog" style="margin-top:6px">
              <div class="small">${t('drafts.confirmDelete')}</div>
              <div style="display:flex;gap:8px;margin-top:6px">
                <button class="btn danger draft-delete-yes" @click=${() => this.removeDraft(d)}>${t('drafts.delete')}</button>
                <button class="btn draft-delete-no" @click=${() => (this.confirmId = '')}>${t('drafts.keep')}</button>
              </div>
            </div>`
          : nothing}
      </div>
    `;
  }

  private openCompleted(c: CompletedDraft, replace = false) {
    if (!c.momentId) return;
    dismissCompleted(c.id);
    // A draft that became a moment lands in the town too (ticket 41).
    go(`/land/${c.momentId}`, replace);
  }

  /**
   * One draft's own action: a local-only draft "Send now", a blocked one
   * "Check now". Either way the run asks the server about this draft's own id
   * first — the same eligibility the automatic path uses, and the one answer
   * that settles a lesson that was made while the answer was lost — and the
   * draft stays on this device whenever the server refuses or the connection
   * is not there.
   */
  private tryDraft = async (d: Draft) => {
    this.busyId = d.id;
    this.confirmId = '';
    this.error = '';
    this.notice = '';
    try {
      const r = await sendDrafts({ only: d.id });
      const hit = r.completed.find((c) => c.id === d.id && c.momentId);
      // Still on screen (the record screen, or home's panel): the moment it became lands now.
      if (hit && this.isConnected) {
        this.openCompleted(hit, true);
        return;
      }
      this.explain(r);
    } finally {
      this.busyId = '';
    }
  };

  private tryAll = async () => {
    this.busyId = '__all__';
    this.confirmId = '';
    this.error = '';
    this.notice = '';
    try {
      this.explain(await sendDrafts({ force: true }));
    } finally {
      this.busyId = '';
    }
  };

  private explain(r: SendResult) {
    const made = r.completed.filter((c) => c.momentId).length;
    const reconciled = r.reconciledIds.length;
    if (made > 0 && reconciled === made) this.notice = made === 1 ? t('drafts.reconciledOne') : t('drafts.reconciledMany', { n: made });
    else if (made > 0) this.notice = made === 1 ? t('record.uploadedOne') : t('record.uploadedMany', { n: made });
    else if (r.offline) this.error = t('home.stillOffline');
    else if (r.skipped) this.error = t('drafts.needsAccount');
    else if (r.blocked) this.notice = t('drafts.stillWaiting');
    void this.loadAllowance();
  }

  /** Deleting a draft never makes a request: it only leaves this device. */
  private removeDraft = async (d: Draft) => {
    this.confirmId = '';
    this.busyId = d.id;
    try {
      if (await deleteDraft(d.id)) {
        this.notice = t('drafts.deleted');
        if (d.id === this.draftId) {
          this.draftId = '';
          this.draftKind = undefined;
          this.draftReason = undefined;
        }
      }
    } finally {
      this.busyId = '';
    }
  };

  /** A draft became a lesson in the background: open it if it is the one on screen. */
  private onDraftsSent = (e: Event) => {
    const done = (e as CustomEvent<CompletedDraft[]>).detail ?? [];
    void this.loadAllowance();
    const hit = this.draftId ? done.find((c) => c.id === this.draftId && c.momentId) : undefined;
    if (!hit) return;
    this.forgetCapture();
    this.openCompleted(hit, true);
  };
}
